import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import './Cleaners.css';
import { Trash2 } from 'lucide-react';

function Cleaners() {
  const [cleaners, setCleaners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [initials, setInitials] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Generate a random hex color
  const generateRandomColor = () => {
    // Generate a color in the middle brightness range (not too dark or too light)
    const getMiddleRangeHex = () => {
      // Generate a number between 80 and 200 (avoiding very dark or very light colors)
      return Math.floor(Math.random() * 120 + 80).toString(16).padStart(2, '0');
    };
    
    const r = getMiddleRangeHex();
    const g = getMiddleRangeHex();
    const b = getMiddleRangeHex();
    
    return `#${r}${g}${b}`;
  };

  // Fetch cleaners from Supabase
  const fetchCleaners = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('cleaners')
        .select('*')
        .order('first_name', { ascending: true });

      if (error) throw error;
      
      // Make sure each cleaner has a properly formatted color
      const formattedData = data?.map(cleaner => ({
        ...cleaner,
        // Ensure color exists and has proper format for backward compatibility
        color: cleaner.color ? (cleaner.color.startsWith('#') ? cleaner.color : cleaner.color) : generateRandomColor().replace('#', '')
      })) || [];
      
      setCleaners(formattedData);
    } catch (error) {
      console.error('Error fetching cleaners:', error.message);
      setError('Failed to load cleaners. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a new cleaner
  const addCleaner = async () => {
    if (!firstName.trim()) {
      setError('First name is required.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Generate initials if not provided
      let cleanerInitials = initials.trim();
      if (!cleanerInitials) {
        cleanerInitials = (firstName.charAt(0) + (lastName ? lastName.charAt(0) : '')).toUpperCase();
      }
      
      // Generate a random color and remove the "#" character
      const colorWithHash = generateRandomColor();
      const color = colorWithHash.replace('#', '');
      
      const { data, error } = await supabase
        .from('cleaners')
        .insert([
          { 
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            initials: cleanerInitials,
            color: color
          }
        ])
        .select();

      if (error) throw error;
      
      setCleaners([...cleaners, data[0]]);
      setFirstName('');
      setLastName('');
      setInitials('');
      setSuccess('Cleaner added successfully!');
      setError(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error adding cleaner:', error.message);
      setError('Failed to add cleaner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Remove a cleaner
  const removeCleaner = async (id) => {
    if (!window.confirm('Are you sure you want to remove this cleaner?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      
      const { error } = await supabase
        .from('cleaners')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setCleaners(cleaners.filter(cleaner => cleaner.id !== id));
      setSuccess('Cleaner removed successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error removing cleaner:', error.message);
      setError('Failed to remove cleaner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCleaners();
  }, []);

  return (
    <div className="cleaners-container">
      {/* <div className="cleaners-header">
        <h1>Cleaners Management</h1>
      </div> */}
      
      <div className="cleaners-content-wrapper">
        {/* Add Cleaner Form */}
        <div className="add-cleaner-form">
          <h2>Add New Cleaner</h2>
          
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          
          <div className="form-group">
            <label htmlFor="firstName">First Name *</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Enter first name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              type="text"
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Enter last name"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="initials">Initials (Optional)</label>
            <input
              type="text"
              id="initials"
              value={initials}
              onChange={(e) => setInitials(e.target.value)}
              placeholder="E.g., JD"
              maxLength={3}
            />
            <small>Leave blank to auto-generate from name</small>
          </div>
          
          <button 
            className="add-button" 
            onClick={addCleaner} 
            disabled={isLoading || !firstName.trim()}
          >
            Add Cleaner
          </button>
        </div>
        
        {/* Cleaners List */}
        <div className="cleaners-list">
          <h2>Cleaners List</h2>
          
          {isLoading && <div className="loading">Loading...</div>}
          
          {!isLoading && cleaners.length === 0 && (
            <div className="no-cleaners">No cleaners found. Add your first cleaner above.</div>
          )}
          
          {cleaners.length > 0 && (
            <div className="cleaners-grid">
              {cleaners.map((cleaner) => (
                <div className="cleaner-card" key={cleaner.id}>
                  <div 
                    className="cleaner-color" 
                    style={{ backgroundColor: cleaner.color.startsWith('#') ? cleaner.color : `#${cleaner.color}` }}
                  >
                    <span>{cleaner.initials}</span>
                  </div>
                  <div className="cleaner-info">
                    <h3>{cleaner.first_name} {cleaner.last_name}</h3>
                    <button 
                      className="remove-button" 
                      onClick={() => removeCleaner(cleaner.id)}
                      title="Remove cleaner"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Cleaners; 